import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  getNotificationStreamUrl,
} from "./notificationApi";

const MAX_ITEMS = 30; // เก็บในหน่วยความจำแค่พอแสดงในกระดิ่ง

/**
 * โหลดแจ้งเตือนครั้งแรก + ต่อ Server-Sent Events เพื่อรับแบบ real-time
 * คืน { items, unreadCount, loading, markRead, markAllRead, refresh }
 */
export default function useNotifications() {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const esRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: MAX_ITEMS });
      setItems(data?.items || []);
      setUnreadCount(data?.unreadCount || 0);
    } catch (e) {
      // เงียบไว้ ไม่ให้กระดิ่งพังทั้งหน้า
      console.error("load notifications failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // เพิ่มแจ้งเตือนใหม่จาก stream (กันซ้ำด้วย NotificationId)
  const prepend = useCallback((notification) => {
    if (!notification || !notification.NotificationId) return;
    setItems((prev) => {
      if (prev.some((n) => n.NotificationId === notification.NotificationId)) return prev;
      return [notification, ...prev].slice(0, MAX_ITEMS);
    });
    setUnreadCount((c) => c + 1);
  }, []);

  const markRead = useCallback(async (id) => {
    setItems((prev) =>
      prev.map((n) => (n.NotificationId === id && !n.IsRead ? { ...n, IsRead: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      const data = await markNotificationRead(id);
      if (data && typeof data.unreadCount === "number") setUnreadCount(data.unreadCount);
    } catch (e) {
      console.error("mark read failed", e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => (n.IsRead ? n : { ...n, IsRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch (e) {
      console.error("mark all read failed", e);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    refresh();

    // ต่อ SSE
    let closed = false;
    const url = getNotificationStreamUrl(token);
    const es = new EventSource(url);
    esRef.current = es;

    const onNotification = (evt) => {
      try {
        prepend(JSON.parse(evt.data));
      } catch (e) {
        console.error("parse notification failed", e);
      }
    };
    // backend ตั้งชื่อ event เป็น 'notification' (ping จะถูกมองข้าม)
    es.addEventListener("notification", onNotification);
    es.onerror = () => {
      // EventSource จะ reconnect ให้เองอัตโนมัติ — แค่ log ไว้
      if (!closed) console.warn("notification stream error, will retry");
    };

    return () => {
      closed = true;
      es.removeEventListener("notification", onNotification);
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, unreadCount, loading, markRead, markAllRead, refresh };
}
